import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Bell, BellOff, Trash2, Plus } from 'lucide-react';
import { notificationService, ScheduledNotification } from '@/services/notificationService';
import { addDays, addHours, addMinutes, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

interface CalendarNotificationDemoProps {
  className?: string;
}

const CalendarNotificationDemo: React.FC<CalendarNotificationDemoProps> = ({ className }) => {
  const [hasPermission, setHasPermission] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [scheduledNotifications, setScheduledNotifications] = useState<ScheduledNotification[]>([]);
  const [eventTitle, setEventTitle] = useState('');
  const [eventType, setEventType] = useState<'appointment' | 'task' | 'deadline'>('appointment');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Verificar suporte e permissões
    setIsSupported(notificationService.getIsSupported());
    setHasPermission(notificationService.isPermissionGranted());
    
    // Carregar notificações agendadas
    loadScheduledNotifications();
  }, []);

  const loadScheduledNotifications = () => {
    const notifications = notificationService.getScheduledNotifications();
    setScheduledNotifications(notifications);
  };

  const requestPermission = async () => {
    setIsLoading(true);
    try {
      const granted = await notificationService.requestPermission();
      setHasPermission(granted);
      
      if (granted) {
        toast.success('Permissão de notificação concedida!');
      } else {
        toast.error('Permissão de notificação negada. Verifique as configurações do navegador.');
      }
    } catch (error) {
      toast.error('Erro ao solicitar permissão de notificação');
    } finally {
      setIsLoading(false);
    }
  };

  const scheduleCalendarEvent = async () => {
    if (!eventTitle.trim() || !eventDate || !eventTime) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (!hasPermission) {
      toast.error('Permissão de notificação necessária');
      return;
    }

    setIsLoading(true);
    try {
      const eventDateTime = new Date(`${eventDate}T${eventTime}`);
      const eventId = `event-${Date.now()}`;
      
      const success = await notificationService.scheduleCalendarNotification(
        eventId,
        eventTitle,
        eventDateTime,
        eventType
      );

      if (success) {
        toast.success(`Notificações agendadas para: ${eventTitle}`);
        setEventTitle('');
        setEventDate('');
        setEventTime('');
        loadScheduledNotifications();
      } else {
        toast.error('Erro ao agendar notificações');
      }
    } catch (error) {
      console.error('Erro ao agendar evento:', error);
      toast.error('Erro ao agendar notificações');
    } finally {
      setIsLoading(false);
    }
  };

  const scheduleQuickReminder = async (minutes: number, title: string) => {
    if (!hasPermission) {
      toast.error('Permissão de notificação necessária');
      return;
    }

    try {
      const reminderId = await notificationService.scheduleQuickReminder(
        title,
        `Você pediu este lembrete há ${minutes} minuto(s).`,
        minutes,
        'normal'
      );

      if (reminderId) {
        toast.success(`Pronto! O aviso chega em ${minutes} minutos.`);
        loadScheduledNotifications();
      } else {
        toast.error('Erro ao agendar lembrete');
      }
    } catch (error) {
      console.error('Erro ao agendar lembrete:', error);
      toast.error('Erro ao agendar lembrete');
    }
  };

  const cancelNotification = async (notificationId: string) => {
    try {
      const success = await notificationService.cancelScheduledNotification(notificationId);
      
      if (success) {
        toast.success('Notificação cancelada');
        loadScheduledNotifications();
      } else {
        toast.error('Erro ao cancelar notificação');
      }
    } catch (error) {
      console.error('Erro ao cancelar notificação:', error);
      toast.error('Erro ao cancelar notificação');
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'appointment':
        return <Calendar className="h-4 w-4" />;
      case 'task':
        return <Clock className="h-4 w-4" />;
      case 'deadline':
        return <Bell className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'destructive';
      case 'high':
        return 'default';
      case 'normal':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  if (!isSupported) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="h-5 w-5" />
            Notificações Não Suportadas
          </CardTitle>
          <CardDescription>
            Seu navegador não suporta notificações push ou service workers.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Status das Permissões */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Sistema de Notificações iOS
          </CardTitle>
          <CardDescription>
            Configure notificações para prazos do calendário
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant={hasPermission ? 'default' : 'destructive'}>
                {hasPermission ? 'Permissão Concedida' : 'Permissão Necessária'}
              </Badge>
              <Badge variant="outline">
                {isSupported ? 'Suportado' : 'Não Suportado'}
              </Badge>
            </div>
            {!hasPermission && (
              <Button 
                onClick={requestPermission} 
                disabled={isLoading}
                size="sm"
              >
                {isLoading ? 'Solicitando...' : 'Solicitar Permissão'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Agendar Evento do Calendário */}
      <Card>
        <CardHeader>
          <CardTitle>Agendar Evento do Calendário</CardTitle>
          <CardDescription>
            Crie um evento e receba notificações 1 dia antes e no dia do evento às 8h
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="eventTitle">Título do Evento</Label>
              <Input
                id="eventTitle"
                placeholder="Ex: Reunião com cliente"
                value={eventTitle}
                onChange={(e) => setEventTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eventType">Tipo do Evento</Label>
              <Select value={eventType} onValueChange={(value: any) => setEventType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="appointment">Compromisso</SelectItem>
                  <SelectItem value="task">Tarefa</SelectItem>
                  <SelectItem value="deadline">Prazo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="eventDate">Data do Evento</Label>
              <Input
                id="eventDate"
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="eventTime">Horário do Evento</Label>
              <Input
                id="eventTime"
                type="time"
                value={eventTime}
                onChange={(e) => setEventTime(e.target.value)}
              />
            </div>
          </div>
          <Button 
            onClick={scheduleCalendarEvent} 
            disabled={!hasPermission || isLoading}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            {isLoading ? 'Agendando...' : 'Agendar Notificações'}
          </Button>
        </CardContent>
      </Card>

      {/* Lembretes Rápidos */}
      <Card>
        <CardHeader>
          <CardTitle>Lembrete rápido</CardTitle>
          <CardDescription>
            Receba um aviso neste aparelho daqui a alguns minutos — por exemplo, para retornar uma ligação.
            {!hasPermission && ' Ative as notificações do navegador para usar.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[5, 10, 15, 30].map((minutes) => (
              <Button
                key={minutes}
                variant="outline"
                size="sm"
                onClick={() => scheduleQuickReminder(minutes, 'Lembrete rápido')}
                disabled={!hasPermission}
              >
                Em {minutes} min
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Lista de Notificações Agendadas */}
      <Card>
        <CardHeader>
          <CardTitle>Notificações Agendadas ({scheduledNotifications.length})</CardTitle>
          <CardDescription>
            Notificações que serão exibidas automaticamente
          </CardDescription>
        </CardHeader>
        <CardContent>
          {scheduledNotifications.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              Nenhuma notificação agendada
            </p>
          ) : (
            <div className="space-y-3">
              {scheduledNotifications.map((notification) => (
                <div 
                  key={notification.id} 
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {getTypeIcon(notification.type)}
                    <div>
                      <p className="font-medium">{notification.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(notification.scheduledFor, 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={getPriorityColor(notification.priority)}>
                      {notification.priority}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelNotification(notification.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarNotificationDemo;